import React, { useRef, useMemo, useEffect, useState } from 'react';
import { useScene } from './hooks/useScene';

import { getSlipSheet, PalletConfigObj } from 'blackbox';
import { PalletGroup } from './createPalletGroup';
import { PMREMGenerator } from 'three';
import { useAssets } from '../../context/AssetsContext';
const SCALE = 0.15; // Scale factor for the scene
interface StackSceneProps {
	palletConfig: PalletConfigObj;
	stackIndex: number;
	palletIndex: number;
	bayIndex: number;
	position?: number;
	useHighlights?: boolean;
	zoom?: number;
	enableControls?: boolean;
	autoRotate?: boolean;
	maxDistance?: number;
	minDistance?: number;
}

export default function StackScene({
	palletConfig,
	stackIndex,
	palletIndex,
	bayIndex,
	position,
	useHighlights = true,
	enableControls,
	autoRotate,
	maxDistance = 9000,
	minDistance = 3000,
}: StackSceneProps) {
	const mountRef = useRef<HTMLDivElement>(null);
	const [totalHeight, setTotalHeight] = useState(0);
	const assets = useAssets();

	// ------------------------------------------------------------
	// Calculate Stack Height to ensure we set bounding size appropriately
	// ------------------------------------------------------------
	const boundingSize = useMemo(() => {
		const pallet = palletConfig.config.pallets[palletIndex];
		const p = pallet.Dimensions;
		return Math.max(p.width, p.length);
	}, [palletConfig, palletIndex]);

	useEffect(() => {
		const pallet = palletConfig.config.pallets[palletIndex];
		let computedTotalHeight = pallet.Dimensions.height;

		const stack = pallet.Stack[stackIndex];
		stack.layers.forEach((layer, layerIndex) => {
			const layout = pallet.Layouts[layer.index];
			const box = palletConfig.config.boxes.find(b => b.id === layout?.box_id);
			if (!box) return;
			computedTotalHeight += box.dimensions.height;

			const slipSheet = getSlipSheet(
				layerIndex,
				stack.layers.length,
				palletConfig.config.slipsheets
			);
			if (slipSheet) {
				computedTotalHeight += slipSheet.dimensions.height;
			}
		});
		setTotalHeight(computedTotalHeight);
	}, [palletConfig, palletIndex, stackIndex]);

	// ------------------------------------------------------------
	// Initialize a 3D scene
	// ------------------------------------------------------------
	const { sceneRef, rendererRef } = useScene({
		mountRef,
		boundingSize: boundingSize,
		objectHeight: totalHeight * SCALE,
		enableControls,
		autoRotate,
		minDistance,
		maxDistance,
		scale: SCALE
	});

	// Rotate to lie flat
	useEffect(() => {
		if (sceneRef.current) {
			sceneRef.current.rotation.x = -Math.PI / 2;
		}
	}, [sceneRef]);

	useEffect(() => {
		if (!sceneRef.current || !rendererRef.current) return;
		const pmremGenerator = new PMREMGenerator(rendererRef.current);
		const envRT = pmremGenerator.fromEquirectangular(assets.hdrTexture!);
		sceneRef.current!.environment = envRT.texture;
		sceneRef.current!.background = null;
		return () => {
			envRT.dispose();
			pmremGenerator.dispose();
		};
	}, [sceneRef, rendererRef]);

	// ------------------------------------------------------------
	// Render the main <div> that holds our canvas...
	// then conditionally render <PalletGroup> once sceneRef.current is ready
	// ------------------------------------------------------------
	return (
		<div
			ref={mountRef}
			style={{
				width: '100%',
				height: '100%',
			}}
		>
			{/* Only render PalletGroup if the scene is created */}
			{sceneRef.current && (
				<PalletGroup
					parentScene={sceneRef.current}
					palletConfig={palletConfig}
					stackIndex={stackIndex}
					palletIndex={palletIndex}
					bayIndex={bayIndex}
					position={position}
					useHighlights={useHighlights}
				/>
			)}
		</div>
	);
}
